/**
 * **Suggested links** — a model reading a note and a shortlist of the operator's other notes, and
 * saying which are about the same thing (APP-8 §3.4).
 *
 * Shared because two parties parse it: the gateway parses what the model returned before it will
 * hand it to anyone, and the device parses what the gateway returned before it will draw a line.
 * Services never import each other (ADR 0001).
 *
 * ## The shape of the request is the privacy decision
 *
 * The device sends a subject and **at most a handful of candidates it chose itself** — never the
 * corpus. That is not an optimisation, it is the whole reason this route can exist at all: the
 * plan deferred server-side suggestions precisely because the naive version means every note body
 * leaves the device, and the size of what leaves grows with the size of the knowledge base. Here
 * it is bounded by `CANDIDATE_MAX`, and the choosing is done by `similarity.ts` on the phone.
 *
 * ## A suggestion names a candidate. It cannot invent one.
 *
 * `toId` is checked against the ids that were SENT, at the gateway, before the response is built.
 * A model that hallucinates an id — or, worse, echoes one it saw in a body — cannot produce an
 * edge to it. The device parses the response again anyway; this is the belt, that is the braces.
 *
 * ## There is no `modelSelection` here, on purpose
 *
 * `/structure` takes one (APP-11) and this does not. Suggestions run on whatever the DEPLOYMENT
 * is configured with. That is a real limitation and it is stated rather than papered over: the
 * alternative considered was accepting the field and serving the default anyway, which is the
 * precise failure this file's sibling warns about — *"a field the server ignores is one that
 * eventually gets trusted by accident"*. Honouring the operator's pick here means giving the
 * suggester the same per-provider adapter matrix the structurer has; that is its own change, and
 * it has a backlog row rather than a half-implemented parameter.
 */
import { z } from 'zod';

/**
 * How many candidates one request may carry.
 *
 * The ceiling on what leaves the device per suggestion and on what one suggestion costs — both
 * unbounded without it. It matches `CANDIDATE_LIMIT` in the device's `similarity.ts`; the two are
 * stated separately because one is a wire bound the server enforces on anyone and the other is a
 * client's own policy, and a server that trusted the client's number would have no bound at all.
 */
export const CANDIDATE_MAX = 8;

/** Bodies are bounded exactly as `/structure` bounds its `text` — same data, same limit. */
const TEXT_MAX = 20_000;
const TITLE_MAX = 200;

/** One note, as offered for comparison. Titles and bodies only — no dates, no type, no owner. */
export const SuggestNoteSchema = z.object({
  id: z.string().min(1).max(64),
  title: z.string().max(TITLE_MAX),
  text: z.string().max(TEXT_MAX),
});
export type SuggestNote = z.infer<typeof SuggestNoteSchema>;

/**
 * `POST /api/multi/v1/suggest-links` — which of these are about the same thing as this one?
 *
 * The owner is deliberately NOT a field, exactly as on `/structure`: it comes from the verified
 * Access identity and nowhere else (ADR 0117 §4). A request field the server must ignore is a
 * field that eventually gets trusted by accident.
 */
export const MultiSuggestLinksRequestSchema = z.object({
  subject: SuggestNoteSchema,
  /**
   * At least one, or there is nothing to choose between and the request is a wasted round trip.
   * The device already declines to ask when its own scoring found nothing; this makes the same
   * rule the server's, so a future caller cannot spend money to be told no.
   */
  candidates: z.array(SuggestNoteSchema).min(1).max(CANDIDATE_MAX),
});
export type MultiSuggestLinksRequest = z.infer<typeof MultiSuggestLinksRequestSchema>;

/**
 * One proposed connection.
 *
 * `reason` is required and non-empty on purpose. A line the operator did not draw, appearing in
 * their own knowledge base, has to be able to say why it is there — otherwise the only available
 * reading is "the machine did something", and the honest response to that is to distrust every
 * other edge too. It is bounded short because it is a phrase on a panel row, not an essay.
 */
export const LinkSuggestionSchema = z.object({
  toId: z.string().min(1).max(64),
  reason: z.string().min(1).max(200),
});
export type LinkSuggestion = z.infer<typeof LinkSuggestionSchema>;

/**
 * Which suggester answered — the same honesty rule as `/structure`'s `engine`.
 *
 * `heuristic` is the deterministic on-server suggester used when no model credential is present.
 * **It is not a mock**: it re-scores the candidates the device sent and explains itself in terms
 * of the shared words it actually found. Named on every response rather than only on `/health`,
 * because this fleet has shipped a stub that passed for a model before.
 */
export const MultiSuggestEngineSchema = z.enum(['model', 'heuristic']);
export type MultiSuggestEngine = z.infer<typeof MultiSuggestEngineSchema>;

export const MultiSuggestLinksResponseSchema = z.object({
  /**
   * MAY BE EMPTY, and empty is the answer that matters most.
   *
   * The device offers candidates on lexical overlap, which finds notes that share rare words but
   * cannot tell "both about the kadaster registry" from "both happen to say kadaster once". The
   * model's job is to say no to the second kind, and a response shape that pushed it towards
   * returning something would destroy the only value this route adds over the scoring that
   * already happened for free on the phone.
   */
  suggestions: z.array(LinkSuggestionSchema).max(CANDIDATE_MAX),
  engine: MultiSuggestEngineSchema,
});
export type MultiSuggestLinksResponse = z.infer<typeof MultiSuggestLinksResponseSchema>;
